import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorBoundary from '../components/ErrorBoundary'
import Gallery, { MEDIA_BASE_URL, buildGalleryImages } from '../components/Gallery'
import GalleryContent from '../components/GalleryContent'
import { resolveMediaSrc } from '../components/RichHtml'
import { flightLogsAPI, adminAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import './FlightLogsPage.css'

/**
 * Card thumbnail with fallback: use featured_image if set, otherwise fetch the
 * first image from the log's gallery folder manifest (mirrors the homepage
 * RecentFlightLogs card). Rendered only when there's no featured_image.
 */
function CardThumb({ log }) {
  const [thumb, setThumb] = useState(null)
  useEffect(() => {
    let cancelled = false
    if (!log.gallery_folder) return
    const url = `${MEDIA_BASE_URL}/images/${log.gallery_folder.split('/').map(encodeURIComponent).join('/')}/manifest.json`
    fetch(url)
      .then((r) => (r.ok ? r.json() : []))
      .then((files) => {
        if (cancelled || !Array.isArray(files) || files.length === 0) return
        const first = buildGalleryImages(log.gallery_folder, [files[0]])[0]
        setThumb(first?.thumb || first?.src || null)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [log.gallery_folder])

  if (!thumb) return null
  return (
    <div className="card-image">
      <img src={thumb} alt={log.title} loading="lazy" />
    </div>
  )
}

function FlightLogsPage() {  const navigate = useNavigate()
  const { logId } = useParams()
  const { isAuthenticated, user, isAdmin } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  // State
  const [logs, setLogs] = useState([])
  const [selectedLog, setSelectedLog] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1, totalPages: 1, total: 0, hasNext: false, hasPrev: false
  })

  // Search/filter state. searchTerm/author/year/site are derived directly from
  // the URL (the single source of truth); only searchInput is local editable
  // state (the text box before the user submits).
  const searchTerm = searchParams.get('search') || ''
  const authorFilter = searchParams.get('author') || ''
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '')
  const yearFilter = searchParams.get('year') || ''
  const [years, setYears] = useState([]) // [{year, count}]
  const siteFilter = searchParams.get('site') || ''
  const [sites, setSites] = useState([]) // [{site, count}]

  // Admin moderation queue (pending flight logs)
  const [pending, setPending] = useState([])
  const [publishing, setPublishing] = useState(null) // log_id currently being published

  // View mode
  const viewMode = logId ? 'detail' : 'list'

  // Load flight logs listing
  const loadLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true)
      setError(null)
      const result = await flightLogsAPI.getFlightLogs(page, 10, {
        search: searchTerm,
        author: authorFilter,
        year: yearFilter,
        site: siteFilter
      })
      setLogs(result.logs || [])
      setPagination(result.pagination || { page: 1, totalPages: 1, total: 0 })
      if (Array.isArray(result.years)) setYears(result.years)
      if (Array.isArray(result.sites)) setSites(result.sites)
    } catch (err) {
      setError(err.message)
      setLogs([])
    } finally {
      setLoading(false)
    }
  }, [searchTerm, authorFilter, yearFilter, siteFilter])

  // Load pending (unpublished) flight logs for admin moderation.
  const loadPending = useCallback(async () => {
    if (!isAdmin) return
    try {
      const items = await adminAPI.getPendingFlightLogs()
      setPending(items || [])
    } catch {
      // Non-fatal: moderation queue just won't show.
      setPending([])
    }
  }, [isAdmin])

  // Publish a pending flight log (admin), then refresh queue + listing.
  const publishLog = async (logId) => {
    setPublishing(logId)
    try {
      await flightLogsAPI.setFlightLogStatus(logId, 'PUBLISHED')
      setPending((prev) => prev.filter((l) => l.log_id !== logId))
      loadLogs(pagination.page || 1)
    } catch (err) {
      setError(err.message)
    } finally {
      setPublishing(null)
    }
  }

  // Admin: unpublish a currently-published log (back to pending).
  const unpublishLog = async (logId) => {
    if (!window.confirm('Unpublish this flight log? It will no longer be visible to the public.')) return
    try {
      await flightLogsAPI.setFlightLogStatus(logId, 'PENDING_APPROVAL')
      navigate('/flight-logs')
    } catch (err) {
      setError(err.message)
    }
  }

  // Delete a flight log (admin, or the log's own author).
  const deleteLog = async (logId) => {
    if (!window.confirm('Delete this flight log? This cannot be undone.')) return
    try {
      await flightLogsAPI.deleteFlightLog(logId)
      navigate('/flight-logs')
    } catch (err) {
      setError(err.message)
    }
  }

  // Load individual flight log
  const loadFlightLog = useCallback(async (id) => {
    try {
      setLoading(true)
      setError(null)
      const log = await flightLogsAPI.getFlightLog(id)
      setSelectedLog(log)
    } catch (err) {
      setError(err.message)
      setSelectedLog(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Keep the search text box in sync with the URL (covers back/forward
  // navigation and direct links). The other filters are derived from the URL
  // during render, so only searchInput (local editable state) needs syncing.
  useEffect(() => {
    let ignore = false
    ;(async () => {
      await Promise.resolve()
      if (!ignore) setSearchInput(searchParams.get('search') || '')
    })()
    return () => { ignore = true }
  }, [searchParams])

  // Load data on mount and when params change
  useEffect(() => {
    let ignore = false
    ;(async () => {
      // Yield a microtask so the loaders' synchronous loading resets aren't set
      // within the effect body; behaviour (and concurrency) is unchanged.
      await Promise.resolve()
      if (ignore) return
      if (logId) {
        loadFlightLog(logId)
      } else {
        const page = parseInt(searchParams.get('page')) || 1
        loadLogs(page)
        loadPending()
      }
    })()
    return () => { ignore = true }
  }, [logId, loadFlightLog, loadLogs, loadPending, searchParams])

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault()
    const params = new URLSearchParams()
    if (searchInput) params.set('search', searchInput)
    if (authorFilter) params.set('author', authorFilter)
    if (yearFilter) params.set('year', yearFilter)
    if (siteFilter) params.set('site', siteFilter)
    setSearchParams(params)
  }

  // Handle year filter change (resets to page 1)
  const handleYearChange = (year) => {
    const params = new URLSearchParams()
    if (searchTerm) params.set('search', searchTerm)
    if (authorFilter) params.set('author', authorFilter)
    if (year) params.set('year', year)
    if (siteFilter) params.set('site', siteFilter)
    setSearchParams(params)
  }

  // Handle site filter change (resets to page 1)
  const handleSiteChange = (site) => {
    const params = new URLSearchParams()
    if (searchTerm) params.set('search', searchTerm)
    if (authorFilter) params.set('author', authorFilter)
    if (yearFilter) params.set('year', yearFilter)
    if (site) params.set('site', site)
    setSearchParams(params)
  }

  // Handle page change
  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', newPage.toString())
    setSearchParams(params)
    window.scrollTo(0, 0)
  }

  // Clear filters
  const clearFilters = () => {
    setSearchInput('')
    setSearchParams({})
  }

  // Navigate to log detail
  const openLog = (log) => {
    navigate(`/flight-logs/${log.log_id}`)
  }

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric'
      })
    } catch {
      return dateStr
    }
  }

  // --- DETAIL VIEW ---
  if (viewMode === 'detail') {
    if (loading) {
      return (
        <div className="page-container">
          <div className="page-content">
            <LoadingSpinner message="Loading flight log..." />
          </div>
        </div>
      )
    }

    if (error || !selectedLog) {
      return (
        <div className="page-container">
          <div className="page-header flight-logs-header">
            <h1>Flight Log Not Found</h1>
            <p>{error || 'This flight log may have been removed or is not yet published.'}</p>
          </div>
          <div className="page-content">
            <button className="back-btn" onClick={() => navigate('/flight-logs')}>
              ← Back to Flight Logs
            </button>
          </div>
        </div>
      )
    }

    return (
      <ErrorBoundary>
        <div className="page-container flight-log-detail">
          <div className="page-header flight-logs-header">
            <h1>{selectedLog.title}</h1>
            <div className="log-meta-header">
              <span className="log-author">{selectedLog.author_name}</span>
              {selectedLog.flight_date && (
                <span className="log-date">{formatDate(selectedLog.flight_date)}</span>
              )}
              {selectedLog.site_name && (
                <span className="log-site">{selectedLog.site_name}</span>
              )}
            </div>
          </div>

          <div className="page-content">
            <div className="flight-log-detail-actions">
              <button className="back-btn" onClick={() => navigate('/flight-logs')}>
                ← Back to Flight Logs
              </button>
              {isAuthenticated && (isAdmin || selectedLog.author_id === user?.userId) && (
                <button className="btn btn-secondary" onClick={() => navigate(`/flight-logs/${selectedLog.log_id}/edit`)}>
                  Edit
                </button>
              )}
              {isAuthenticated && isAdmin && (
                <button className="btn btn-secondary" onClick={() => unpublishLog(selectedLog.log_id)}>
                  Unpublish
                </button>
              )}
              {isAuthenticated && (isAdmin || selectedLog.author_id === user?.userId) && (
                <button className="btn btn-danger" onClick={() => deleteLog(selectedLog.log_id)}>
                  Delete
                </button>
              )}
            </div>

            <article className="flight-log-article">
              {selectedLog.featured_image && (
                <div className="log-featured-image">
                  <img src={resolveMediaSrc(selectedLog.featured_image)} alt={selectedLog.title} loading="lazy" />
                </div>
              )}

              <div className="log-body">
                {selectedLog.introtext && (
                  <div className="log-intro">
                    <GalleryContent content={selectedLog.introtext} preserveLinks={true} />
                  </div>
                )}
                {selectedLog.content && selectedLog.content !== selectedLog.introtext && (
                  <div className="log-content">
                    <GalleryContent content={selectedLog.content} preserveLinks={true} />
                  </div>
                )}
              </div>

              {selectedLog.images && selectedLog.images.length > 0 && (
                <div className="log-gallery">
                  <Gallery
                    images={selectedLog.images.map((img, idx) => ({
                      src: typeof img === 'string' ? img : img.src,
                      alt: typeof img === 'string' ? `${selectedLog.title} photo ${idx + 1}` : img.alt,
                      thumb: typeof img === 'object' ? img.thumb : img
                    }))}
                    title="Photos"
                    columns={3}
                  />
                </div>
              )}

              <div className="log-footer">
                <div className="log-stats">
                  {selectedLog.hits > 0 && (
                    <span className="log-views">{selectedLog.hits.toLocaleString()} views</span>
                  )}
                  <span className="log-published">Published {formatDate(selectedLog.created_date)}</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </ErrorBoundary>
    )
  }

  // --- LIST VIEW ---
  return (
    <ErrorBoundary>
      <div className="page-container">
        <div className="page-header flight-logs-header">
          <h1>Flight Logs</h1>
          <p>Stories and adventures from the Wingbeat community</p>
          {isAuthenticated && (
            <button className="btn btn-primary flight-log-new-btn" onClick={() => navigate('/flight-logs/new')}>
              + Add flight log
            </button>
          )}
        </div>

        <div className="page-content">
          {/* Admin moderation queue: pending flight logs */}
          {isAdmin && pending.length > 0 && (
            <div className="flightlog-moderation">
              <h2>Pending approval ({pending.length})</h2>
              <ul className="moderation-list">
                {pending.map((p) => (
                  <li key={p.log_id} className="moderation-item">
                    <div className="moderation-meta">
                      <strong>{p.title || '(untitled)'}</strong>
                      <span> — {p.author_name || 'unknown'}</span>
                      {p.site_name && <span> • {p.site_name}</span>}
                      {p.flight_date && <span> • {formatDate(p.flight_date)}</span>}
                    </div>
                    <div className="moderation-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate(`/flight-logs/${p.log_id}/edit`)}
                      >
                        Review
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={publishing === p.log_id}
                        onClick={() => publishLog(p.log_id)}
                      >
                        {publishing === p.log_id ? 'Publishing…' : 'Publish'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Search and Filters */}
          <div className="flight-logs-controls">
            <form onSubmit={handleSearch} className="search-form">
              <input
                type="text"
                placeholder="Search flight logs (title, story, site, author)…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="search-btn">Search</button>
              {years.length > 0 && (
                <select
                  className="year-filter"
                  value={yearFilter}
                  onChange={(e) => handleYearChange(e.target.value)}
                  aria-label="Filter by year"
                >
                  <option value="">All years</option>
                  {years.map((y) => (
                    <option key={y.year} value={y.year}>{y.year} ({y.count})</option>
                  ))}
                </select>
              )}
              {sites.length > 0 && (
                <select
                  className="year-filter site-filter"
                  value={siteFilter}
                  onChange={(e) => handleSiteChange(e.target.value)}
                  aria-label="Filter by site"
                >
                  <option value="">All sites</option>
                  {sites.map((s) => (
                    <option key={s.site} value={s.site}>{s.site} ({s.count})</option>
                  ))}
                </select>
              )}
              {(searchTerm || authorFilter || yearFilter || siteFilter) && (
                <button type="button" className="clear-btn" onClick={clearFilters}>
                  Clear
                </button>
              )}
            </form>

            {pagination.total >= 0 && (
              <div className="results-info">
                <span>{pagination.total} flight log{pagination.total !== 1 ? 's' : ''}</span>
                {searchTerm && <span className="filter-tag">Search: "{searchTerm}"</span>}
                {siteFilter && <span className="filter-tag">Site: {siteFilter}</span>}
                {yearFilter && <span className="filter-tag">Year: {yearFilter}</span>}
                {authorFilter && <span className="filter-tag">Author: {authorFilter}</span>}
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading && <LoadingSpinner message="Loading flight logs..." />}

          {/* Error State */}
          {error && !loading && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => loadLogs(1)}>Try Again</button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && logs.length === 0 && (
            <div className="no-results">
              <h3>No flight logs found</h3>
              {searchTerm ? (
                <p>No results for "{searchTerm}". Try a different search term.</p>
              ) : (
                <p>Flight logs will appear here once they are published.</p>
              )}
            </div>
          )}

          {/* Flight Log Cards */}
          {!loading && !error && logs.length > 0 && (
            <div className="flight-logs-list">
              {logs.map(log => (
                <article
                  key={log.log_id}
                  className="flight-log-card"
                  onClick={() => openLog(log)}
                >
                  {log.featured_image ? (
                    <div className="card-image">
                      <img src={resolveMediaSrc(log.featured_image)} alt={log.title} loading="lazy" />
                    </div>
                  ) : (
                    <CardThumb log={log} />
                  )}
                  <div className="card-content">
                    <h2 className="card-title">{log.title}</h2>
                    <div className="card-meta">
                      <span className="card-author">{log.author_name}</span>
                      <span className="card-separator">•</span>
                      <span className="card-date">{formatDate(log.flight_date || log.created_date)}</span>
                      {log.site_name && (
                        <>
                          <span className="card-separator">•</span>
                          <span className="card-site">{log.site_name}</span>
                        </>
                      )}
                    </div>
                    <p className="card-excerpt">{log.excerpt}</p>
                    <div className="card-footer">
                      <span className="read-more">Read more →</span>
                      {log.hits > 0 && (
                        <span className="card-hits">{log.hits.toLocaleString()} views</span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn"
                disabled={!pagination.hasPrev}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                ← Previous
              </button>
              
              <div className="page-numbers">
                {generatePageNumbers(pagination.page, pagination.totalPages).map((pageNum, idx) => (
                  pageNum === '...' ? (
                    <span key={`ellipsis-${idx}`} className="page-ellipsis">...</span>
                  ) : (
                    <button
                      key={pageNum}
                      className={`page-num ${pageNum === pagination.page ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  )
                ))}
              </div>

              <button
                className="page-btn"
                disabled={!pagination.hasNext}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  )
}

/**
 * Generate page number array with ellipsis for large page counts
 */
function generatePageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const pages = []
  pages.push(1)

  if (current > 3) pages.push('...')

  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  if (current < total - 2) pages.push('...')

  pages.push(total)

  return pages
}

export default FlightLogsPage
